/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-param-reassign */
import { toArray, deepMerge, isMergeableObject, isObject, deepClone } from '../util/data';
import { type UEDToken, type EChartsTheme } from '../core/token';
import { type LegendTheme } from '../ui/legend';
import type { Config as TooltipConfig } from '../hook/preset/buildTooltip';
import type { Insight } from '../insight/insightManager';

export type StandardChartIns = {
  play: (renderParams: { option: unknown; opts?: unknown }) => void;
  getECharts: () => unknown;
  destroy: () => void;
};

export type EChartsModel = {
  getOption(): Option;
};
export type EChartsAPI = {
  getDom(): HTMLElement;
};

type TextStyle = {
  color: string;
  fontFamily: string;
  fontWeight: number | string;
  fontSize: number;
  lineHeight: number;
};

export interface Option {
  /** @see https://echarts.apache.org/zh/option.html#textStyle */
  textStyle?: Partial<TextStyle>;
  backgroundColor?: string;
  color?: string[];
  /** @see https://echarts.apache.org/zh/option.html#legend */
  legend?: LegendTheme;
  /** @see https://echarts.apache.org/zh/option.html#grid */
  grid?:
    | {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
      }
    | {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
      }[];
  /** @see https://echarts.apache.org/zh/option.html#dataZoom */
  dataZoom: {
    type?: 'slider' | 'inside';
    startValue?: number;
    endValue?: number;
    moveOnMouseMove?: boolean;
    zoomOnMouseWheel?: boolean;
    /** 竖直方向dataZoom的滚动条颜色 */
    dvScrollColor?: string;
  }[];
  /** @see https://echarts.apache.org/zh/option.html#tooltip */
  tooltip: Partial<TooltipConfig>;
  /** @see https://echarts.apache.org/zh/option.html#axisPointer */
  axisPointer: { [k: string]: unknown };
  /** @see https://echarts.apache.org/zh/option.html#xAxis */
  xAxis?: Array<{
    /** 使用的数据集 */
    __dataIndex?: number;
    /** 使用的数据集字段 */
    __encodeField?: string;
    type?: string;
    data?: Array<string | number | { value: string | number }>;
    nameLocation?: string;
    nameGap?: number;
    nameTextStyle?: {
      align?: string;
      padding?: [number, number, number, number];
    };
    axisLine?: unknown;
    axisTick?: unknown;
    axisLabel?: unknown;
    splitLine?: unknown;
    axisPointer?: {
      z?: number;
    };
    z?: number;
  }>;
  /** @see https://echarts.apache.org/zh/option.html#yAxis */
  yAxis?: Array<{
    /** 使用的数据集 */
    __dataIndex?: number;
    /** 使用的数据集字段 */
    __encodeField?: string[];
    type?: string;
    data?: Array<string | number | { value: string | number }>;
    nameLocation?: string;
    nameGap?: number;
    nameTextStyle?: {
      align?: string;
      padding?: [number, number, number, number];
    };
    axisLine?: unknown;
    axisTick?: unknown;
    axisLabel?: unknown;
    splitLine?: unknown;
    axisPointer?: {
      z?: number;
    };
    z?: number;
  }>;
  /** @see https://echarts.apache.org/zh/option.html#radar */
  radar?: {
    axisLine?: unknown;
    splitLine?: unknown;
    axisName?: unknown;
    splitArea?: unknown;
    axisPointer?: unknown;
    z?: number;
  };
  /**
   * @see https://echarts.apache.org/zh/option.html#series
   */
  series:
    | {
        type: string;
        /** 使用的数据集字段 */
        __encodeField: string;
        xAxisIndex?: number;
        yAxisIndex?: number;
        name: string;
        data: Array<string | number | { value: string | number }>;
        itemStyle?: { color?: string };
        animationDuration?: number;
        markPoint?: { data?: unknown[]; animationDuration?: number };
        markLine?: { symbol?: string; data?: unknown[]; animationDuration?: number };
        markArea?: { data?: unknown[]; animationDuration?: number };
      }
    | {
        type: string;
        /** 使用的数据集字段 */
        __encodeField: string;
        xAxisIndex?: number;
        yAxisIndex?: number;
        name: string;
        data: Array<string | number | { value: string | number }>;
        itemStyle?: { color?: string };
        animationDuration?: number;
        markPoint?: { data?: unknown[]; animationDuration?: number };
        markLine?: { symbol?: string; data?: [unknown, unknown][]; animationDuration?: number };
        markArea?: { data?: unknown[]; animationDuration?: number };
      }[];
  dvInsight?: Insight[];
}

export interface PlayOpts {
  notMerge: boolean;
}

type ExtractArrayType<T> = T extends Array<infer U> ? Array<U> : never;

type TypeWithOnlyArray<T> = {
  [K in keyof T]: ExtractArrayType<T[K]>;
};

export function normalizeOption<T extends Partial<Option>>(option: T): TypeWithOnlyArray<T> {
  const result = { ...option };
  if (typeof result.grid !== 'undefined' && result.grid) {
    result.grid = toArray(result.grid);
  }
  if (typeof result.dataZoom !== 'undefined' && result.dataZoom) {
    result.dataZoom = toArray(result.dataZoom);
  }
  if (typeof result.xAxis !== 'undefined' && result.xAxis) {
    // @ts-ignore
    result.xAxis = toArray(result.xAxis);
  }
  if (typeof result.yAxis !== 'undefined' && result.yAxis) {
    // @ts-ignore
    result.yAxis = toArray(result.yAxis);
  }
  if (typeof result.series !== 'undefined' && result.series) {
    // @ts-ignore
    result.series = toArray(result.series);
  }

  return result as TypeWithOnlyArray<T>;
}

export function getDataItemValue(
  data: number | string | { value: number | string | number[] | string[] }
) {
  if (typeof data !== null && typeof data === 'object') {
    return data.value;
  }

  return data;
}

export function findXAxisIndex(
  option: Option,
  metaInfo: {
    xField: string;
  }
) {
  return toArray(option.xAxis).findIndex(axis => axis.__encodeField === metaInfo.xField);
}

function findYAxisIndex(
  option: Option,
  metaInfo: {
    yField: string;
  }
) {
  return toArray(option.yAxis).findIndex(axis =>
    toArray(axis.__encodeField).includes(metaInfo.yField)
  );
}

function findSeriesDataItemInfoWithoutXY(
  option: Option,
  metaInfo: {
    xField: string;
    yField: string;
    xValue?: string;
    yValue?: string;
    seriesName?: string;
  }
) {
  const seriesIndex = toArray(option.series).findIndex(
    series =>
      series?.AIGCEncoding?.x === metaInfo.xField && series?.AIGCEncoding?.y === metaInfo.yField
  );

  if (seriesIndex === -1) {
    return null;
  }

  const seriesData = toArray(option.series)[seriesIndex].data;

  let dataItemIndex = seriesData.findIndex(dataItem => {
    return dataItem?.name === metaInfo.xValue && dataItem?.value === metaInfo.yValue;
  });

  if (dataItemIndex === -1) {
    dataItemIndex = 0;
  }

  return { seriesIndex, xAxisIndex: null, yAxisIndex: null, dataItemIndex };
}

/**
 * 根据元信息查找对应的数据项信息
 */
export function findSeriesDataItemInfo(
  option: Option,
  metaInfo: {
    xField: string;
    yField: string;
    xValue?: string;
    yValue?: string;
    seriesName?: string;
  }
) {
  const xAxisIndex = findXAxisIndex(option, metaInfo);
  const yAxisIndex = findYAxisIndex(option, metaInfo);
  // 如果没有xy轴，则查找特定series(pie, treemap)的元信息
  if (xAxisIndex === -1 || yAxisIndex === -1) {
    return findSeriesDataItemInfoWithoutXY(option, metaInfo);
  }

  const seriesIndex = toArray(option.series).findIndex(
    series =>
      // eslint-disable-next-line thsjs/expression-complexity
      series.xAxisIndex === xAxisIndex &&
      series.yAxisIndex === yAxisIndex &&
      (series.__encodeField === metaInfo.seriesName ||
        series.__encodeField === metaInfo.yField ||
        series.data.some(dataItem =>
          toArray(getDataItemValue(dataItem)).some(item => item === metaInfo.yValue)
        ))
  );
  if (seriesIndex === -1) {
    return null;
  }

  let dataItemIndex = -1;
  if (typeof metaInfo.xValue === 'undefined') {
    dataItemIndex = 0;
  } else {
    dataItemIndex = toArray(option.xAxis)[xAxisIndex]!.data.findIndex(
      item => getDataItemValue(item) === metaInfo.xValue
    );
  }
  if (dataItemIndex === -1) {
    return null;
  }

  return { xAxisIndex, yAxisIndex, seriesIndex, dataItemIndex };
}

/**
 * @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker
 */
export function getSingletonInsightDvMarkerSeries(
  option: Option,
  metaInfo: { xAxisIndex: number; yAxisIndex: number }
) {
  option.series = toArray(option.series);

  let dvMarkerSeries = option.series.find(
    item =>
      (item as unknown as { __isInsight: boolean }).__isInsight === true &&
      item.type === 'dvMarker' &&
      item.xAxisIndex === metaInfo.xAxisIndex &&
      item.yAxisIndex === metaInfo.yAxisIndex
  );
  if (!dvMarkerSeries) {
    dvMarkerSeries = {
      __isInsight: true,
      type: 'dvMarker',
      xAxisIndex: metaInfo.xAxisIndex,
      yAxisIndex: metaInfo.yAxisIndex,
      dvMarkType: 'curvedPointer',
      data: [],
      silent: true,
      tooltip: {
        show: false
      },
      z: 9999
    };

    option.series.push(dvMarkerSeries);
  }

  return dvMarkerSeries;
}

export function filterInsightSeries(option: Option) {
  return {
    ...option,
    series: toArray(option.series).filter(item => item.__isInsight !== true)
  };
}

function restoreSeriesDataItem<T>(target: T): T {
  if (typeof (target as unknown as { __saveData?: T }).__saveData !== 'undefined') {
    return (target as unknown as { __saveData: T }).__saveData;
  }

  return target;
}

export function mergeSeriesDataItem<T>(target: T, override: unknown): T {
  if (typeof target === null || typeof target !== 'object') {
    target = {
      value: target
    };
  }

  target = restoreSeriesDataItem(target);

  return {
    ...deepMerge(target, override, { override: true }),
    name: target.name,
    value: target.value,
    __saveData: target
  } as T;
}

/**
 * 配置项合并
 *
 * - 不修改 target 对象，返回一个新的对象
 * - 遇到 target 字段为数组时，分多种情况进行处理，请查看测试用例
 *
 * @param option.override - 当 key 同时存在时，是否用 source 的 value 覆盖 target 的 value
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, sonarjs/cognitive-complexity
export function mergeOption(target: any, source: any, option = { override: false }): any {
  return deepMerge(target, source, {
    ...option,
    arrayMerge: (a: any[], b, opt) => {
      let result = a.slice();

      if (!Array.isArray(b)) {
        // * array - object
        a.forEach((item: any, index: number) => {
          result[index] = deepMerge(item, b, opt);
        });
      } else if (Array.isArray(b) && a.length === b.length) {
        // * array.length = array.length
        a.forEach((item: any, index: number) => {
          result[index] = deepMerge(item, b[index], opt);
        });
      } else {
        // * array.length != array.length
        result = opt.override ? b.slice() : result;
      }

      return result as unknown;
    }
  });
}

/**
 * 从 `dataset` 中找到无 `type` 字段的通用配置项，再找到一个 `type` 字段相等的特定配置项，将其合并返回，优先级：有 `type` > 无 `type`
 */
export function findOptionByType(finder: { type: string }, dataset: { type?: string }[] = []) {
  const noTypeItem = dataset.find(item => typeof item.type === 'undefined');
  const sameTypeItem = dataset.find(item => item.type === finder.type);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return mergeOption(deepClone(sameTypeItem), deepClone(noTypeItem));
}

/**
 * 合并除 series 以外的公共配置
 */
export function mergeComponentsOption(
  target: Omit<Option, 'series'>,
  source: Omit<Option, 'series'>,
  option = { override: false }
): Omit<Option, 'series'> {
  return mergeOption(target, source, option) as Omit<Option, 'series'>;
}

export function mergeSeriesOption(
  target: Option['series'],
  source: Option['series'],
  option = { override: false }
): Option['series'] {
  if (Array.isArray(source)) {
    source.map(item => {
      if (!item || !isObject(item)) {
        return item;
      }

      // ! 关键字段不能被用户覆盖
      const { type, name, data, ...overrideOption } = item;
      return overrideOption;
    });
  } else if (isObject(source)) {
    // ! 关键字段不能被用户覆盖
    const { type, name, data, ...overrideOption } = source;
    source = overrideOption;
  } else {
    //
  }

  return mergeOption(target, source, option) as Option['series'];
}

/**
 * 匹配合并
 *
 * - 如果 `target` 的 `value` 中存在 `type` 字段，则从 `source` 中优先找 `type` 字段相同的进行合并
 */
export function matchMergeOption(
  target: Option,
  source: Option,
  option = { override: false }
): Option {
  const normalizedTarget = normalizeOption(target);
  const normalizedSource = normalizeOption(source);
  const result = { ...normalizedSource };

  (Object.keys(normalizedTarget) as unknown as (keyof typeof result)[]).forEach(field => {
    const mergeFunc = field === 'series' ? mergeSeriesOption : mergeComponentsOption;
    const targetValue = normalizedTarget[field];
    const sourceValue = normalizedSource[field];

    if (
      !isMergeableObject(targetValue) ||
      !isMergeableObject(sourceValue) ||
      (Array.isArray(targetValue)
        ? targetValue.every(item => typeof item?.type === 'undefined')
        : typeof targetValue?.type === 'undefined')
    ) {
      // @ts-ignore
      result[field] = mergeFunc(targetValue, sourceValue, option);
    } else if (!Array.isArray(targetValue)) {
      // @ts-ignore
      result[field] = mergeFunc(
        // @ts-ignore
        targetValue,
        findOptionByType(
          targetValue as unknown as { type: string },
          toArray(sourceValue as unknown as { type: string })
        ),
        option
      );
    } else {
      // @ts-ignore
      result[field] = targetValue.map(itemTarget => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return mergeFunc(
          // @ts-ignore
          itemTarget,
          findOptionByType(
            itemTarget as { type: string },
            toArray(sourceValue as unknown as { type: string })
          ),
          option
        );
      });
    }
  });

  return result;
}

export function echartsThemeToOption(themeVars: Partial<EChartsTheme>): Partial<Option> {
  const axis = {
    show: false,
    nameTextStyle: {
      color: themeVars?.subtitleColor
    },
    axisLine: {
      lineStyle: {
        color: themeVars?.axes?.[0]?.axisLineColor
      }
    },
    axisTick: {
      lineStyle: {
        color: themeVars?.axes?.[0]?.axisTickColor
      }
    },
    axisLabel: {
      textStyle: {
        color: themeVars?.axes?.[0]?.axisLabelColor
      }
    },
    splitLine: {
      lineStyle: {
        color: themeVars?.axes?.[0]?.splitLineColor
      }
    },
    dataZoom: {
      fillerColor: themeVars?.datazoomFillColor,
      backgroundColor: themeVars?.datazoomBackgroundColor
    }
  };

  return {
    backgroundColor: themeVars.backgroundColor,
    color: themeVars.color,
    legend: {
      textStyle: {
        color: themeVars.subtitleColor
      }
    },
    dataZoom: {
      textStyle: {
        color: themeVars.datazoomLabelColor
      }
    },
    xAxis: axis,
    yAxis: axis,
    angleAxis: axis,
    // * 3D ---------------
    xAxis3D: axis,
    yAxis3D: axis,
    zAxis3D: axis,
    series: [
      {
        type: 'gauge',
        title: {
          color: themeVars.titleColor
        }
      },
      {
        type: 'treemap',
        itemStyle: {
          borderColor: themeVars.backgroundColor
        }
      }
    ]
  };
}

export function uedTokenToOption(tokenVars: Partial<UEDToken>): Partial<Option> {
  const axis = {
    nameTextStyle: {
      fontWeight: tokenVars['--font-weight-regular'],
      fontSize: tokenVars['--font-size-xxs'],
      lineHeight: tokenVars['--line-height-super-small'],
      color: tokenVars['--color-text-secondary']
    },
    axisLabel: {
      fontSize: tokenVars['--font-size-xxs'],
      lineHeight: tokenVars['--line-height-small'],
      color: tokenVars['--color-text-secondary']
    },
    splitLine: {
      lineStyle: {
        color: tokenVars['--color-visualization-divider']
      }
    },
    axisPointer: {
      label: {
        fontWeight: tokenVars['--font-weight-bold'],
        fontSize: tokenVars['--font-size-xxs'],
        lineHeight: tokenVars['--line-height-super-small']
      },
      lineStyle: {
        color: tokenVars['--color-visualization-tooltip']
      },
      shadowStyle: {
        color: tokenVars['--color-visualization-tooltip']
      }
    }
  };

  return {
    color: tokenVars['--color-visualization'],
    legend: {
      backgroundColor: tokenVars['--color-background-weak'],
      textStyle: {
        fontSize: tokenVars['--font-size-extra-small'],
        lineHeight: tokenVars['--line-height-small'],
        color: tokenVars['--color-text-secondary']
      }
    },
    dataZoom: {
      fillerColor: tokenVars['--color-background-weak']
    },
    xAxis: axis,
    yAxis: axis,
    angleAxis: {
      show: false,
      ...axis
    },
    radiusAxis: {
      show: false,
      ...axis
    },
    xAxis3D: axis,
    yAxis3D: axis,
    zAxis3D: axis,
    series: {
      label: axis.nameTextStyle
    }
  };
}

// 临时处理x轴在顶部的轴名称位置
export function setNameTextStyle($dom: HTMLElement, option: Partial<Option>) {
  option.xAxis[0].nameTextStyle = {
    padding: [-45, 0, 0, 0],
    align: 'right'
  };
  option.yAxis[0].nameTextStyle = {
    padding: [-34, 0, 0, 0]
  };
}

// 创建默认水印对象
export function createDefaultWatermark(
  chartViewRect: {
    width: number;
    height: number;
  },
  text: string | number = ''
) {
  return {
    elements: [
      {
        type: 'text',
        top: 'middle',
        left: 'center',
        style: {
          text,
          font: `bold ${
            Math.min(chartViewRect.width, chartViewRect.height) / 8
          }px THSJinRongTi-Medium`
        },
        z: 100
      }
    ]
  };
}

// 只返回必要配置
export function getRenderOption(option: Option) {
  return {
    xAxis: option.xAxis.map(item => {
      return {
        show: item.show,
        __dataIndex: item.__dataIndex,
        __encodeField: item.__encodeField,
        type: item.type,
        data: item.data
      };
    }),
    yAxis: option.yAxis.map(item => {
      return {
        show: item.show,
        __dataIndex: item.__dataIndex,
        __encodeField: item.__encodeField,
        type: item.type
      };
    }),
    series: option.series
  };
}
